import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, ChevronDown, ExternalLink, FileText, MoreHorizontal, Pencil, Plus, RefreshCw, Star, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { blogCategories } from '../../lib/blog-types';
import { API_ORIGIN } from '../../lib/apiBase';
import { cn } from '../../lib/utils';
import BlogPostEditor from '../../components/admin/BlogPostEditor';
import CaseStudyEditor from '../../components/admin/CaseStudyEditor';
import { EmptyState, PageBar, PrimaryButton, SearchInput, Segmented, ToolButton } from '../../components/admin/dash/Toolbar';
import { StatusTag } from '../../components/admin/editor/EditorShell';
import { ago, shortDay } from '../../components/admin/dash/palette';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const API_BASE = process.env.NODE_ENV === 'production' ? `${API_ORIGIN}/api/blog` : `${API_ORIGIN}/.netlify/functions/blog`;

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt?: string;
  category: string;
  tags: string[];
  featuredImage?: string;
  author?: { id: string; name: string };
  publishedAt?: string;
  updatedAt: string;
  readTime: number;
  featured: boolean;
  status: string;
}

interface CaseStudy {
  id: string;
  slug: string;
  title: string;
  subtitle?: string;
  clientName: string;
  clientLocation?: string;
  industry?: string;
  featuredImage?: string;
  tags: string[];
  status: string;
  publishedAt?: string;
  updatedAt: string;
}

type Tab = 'posts' | 'case-studies';
type StatusFilter = 'all' | 'published' | 'draft';
type SortKey = 'title' | 'publishedAt' | 'updatedAt';
type Row = {
  id: string;
  kind: Tab;
  title: string;
  slug: string;
  image?: string;
  status: string;
  featured?: boolean;
  publishedAt?: string;
  updatedAt: string;
  /** Category (posts) or client (case studies). */
  primary: React.ReactNode;
  secondary: React.ReactNode;
  search: string;
  category?: string;
};

const categoryLabel = (v: string) => blogCategories.find((c) => c.value === v)?.label ?? v;
const livePath = (r: Row) => (r.kind === 'posts' ? `/blog/${r.slug}` : `/case-study/${r.slug}`);

const Thumb: React.FC<{ src?: string }> = ({ src }) => {
  const [broken, setBroken] = useState(false);
  return (
    <span className="grid h-9 w-12 shrink-0 place-items-center overflow-hidden rounded-md border border-[color:var(--oa-line-2)] bg-[color:var(--oa-raised)] text-[color:var(--oa-faint)]">
      {src && !broken ? <img src={src} alt="" loading="lazy" onError={() => setBroken(true)} className="h-full w-full object-cover" /> : <FileText className="h-4 w-4" />}
    </span>
  );
};

const AdminBlogEditor: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get('tab') === 'case-studies' ? 'case-studies' : 'posts';
  const q = params.get('q') ?? '';
  const status = (params.get('status') as StatusFilter) || 'all';
  const cats = (params.get('cat') ?? '').split(',').filter(Boolean);
  const sortKey = (params.get('sort') as SortKey) || 'updatedAt';
  const sortDir = params.get('dir') === 'asc' ? 'asc' : 'desc';
  const editId = params.get('edit');
  const creating = params.get('new') === '1';

  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [caseStudies, setCaseStudies] = useState<CaseStudy[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState<Row | null>(null);
  const [deleting, setDeleting] = useState(false);

  const patch = useCallback(
    (next: Record<string, string | null>, replace = true) => {
      setParams(
        (p) => {
          const out = new URLSearchParams(p);
          Object.entries(next).forEach(([k, v]) => (v === null || v === '' ? out.delete(k) : out.set(k, v)));
          return out;
        },
        { replace }
      );
    },
    [setParams]
  );

  const load = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('admin_token');
    try {
      const [pr, cr] = await Promise.all([
        fetch(`${API_BASE}/admin/posts`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/admin/case-studies`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (pr.status === 401 || cr.status === 401) return navigate('/admin/login', { replace: true });
      const [pd, cd] = await Promise.all([pr.json(), cr.json()]);
      if (pd.success) setPosts(pd.posts);
      if (cd.success) setCaseStudies(cd.caseStudies);
      if (!pd.success || !cd.success) throw new Error(pd.error || cd.error || 'Could not load the blog');
    } catch (err: any) {
      toast.error(err?.message || 'Could not load the blog');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!editId && !creating) load();
  }, [load, editId, creating]);

  const rows: Row[] = useMemo(
    () =>
      tab === 'posts'
        ? posts.map((p) => ({
            id: p.id,
            kind: 'posts' as Tab,
            title: p.title,
            slug: p.slug,
            image: p.featuredImage,
            status: p.status,
            featured: p.featured,
            publishedAt: p.publishedAt,
            updatedAt: p.updatedAt,
            primary: categoryLabel(p.category),
            secondary: p.author?.name ?? '—',
            search: `${p.title} ${p.slug} ${p.excerpt ?? ''} ${(p.tags || []).join(' ')} ${p.author?.name ?? ''}`.toLowerCase(),
            category: p.category,
          }))
        : caseStudies.map((c) => ({
            id: c.id,
            kind: 'case-studies' as Tab,
            title: c.title,
            slug: c.slug,
            image: c.featuredImage,
            status: c.status,
            publishedAt: c.publishedAt,
            updatedAt: c.updatedAt,
            primary: (
              <span className="min-w-0">
                <span className="block truncate">{c.clientName}</span>
                {c.clientLocation ? <span className="block truncate text-[11.5px] text-[color:var(--oa-muted)]">{c.clientLocation}</span> : null}
              </span>
            ),
            secondary: c.industry || '—',
            search: `${c.title} ${c.slug} ${c.clientName} ${c.clientLocation ?? ''} ${c.industry ?? ''} ${(c.tags || []).join(' ')}`.toLowerCase(),
          })),
    [tab, posts, caseStudies]
  );

  const counts = useMemo(
    () => ({
      all: rows.length,
      published: rows.filter((r) => r.status === 'published').length,
      draft: rows.filter((r) => r.status !== 'published').length,
    }),
    [rows]
  );

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const out = rows.filter(
      (r) =>
        (!needle || r.search.includes(needle)) &&
        (status === 'all' || (status === 'published' ? r.status === 'published' : r.status !== 'published')) &&
        (tab !== 'posts' || !cats.length || (r.category && cats.includes(r.category)))
    );
    const dir = sortDir === 'asc' ? 1 : -1;
    return out.sort((a, b) => {
      if (sortKey === 'title') return a.title.localeCompare(b.title) * dir;
      const av = a[sortKey] ? new Date(a[sortKey] as string).getTime() : 0;
      const bv = b[sortKey] ? new Date(b[sortKey] as string).getTime() : 0;
      return (av - bv) * dir;
    });
  }, [rows, q, status, cats, tab, sortKey, sortDir]);

  const openEditor = (id: string | null) => patch(id ? { edit: id, new: null } : { new: '1', edit: null }, false);
  const closeEditor = () => patch({ edit: null, new: null }, false);

  const remove = async () => {
    if (!confirm) return;
    setDeleting(true);
    try {
      const path = confirm.kind === 'posts' ? 'posts' : 'case-studies';
      const res = await fetch(`${API_BASE}/admin/${path}/${confirm.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Could not delete');
      if (confirm.kind === 'posts') setPosts((p) => p.filter((x) => x.id !== confirm.id));
      else setCaseStudies((c) => c.filter((x) => x.id !== confirm.id));
      toast.success(`Deleted “${confirm.title}”`);
      setConfirm(null);
    } catch (err: any) {
      toast.error(err?.message || 'Could not delete');
    } finally {
      setDeleting(false);
    }
  };

  // Editors take over the whole page; their URL is ?edit=<id> or ?new=1.
  if (editId || creating) {
    const common = {
      onCancel: closeEditor,
      onCreated: (id: string) => patch({ edit: id, new: null }),
    };
    return tab === 'posts' ? (
      <BlogPostEditor key={editId ?? 'new'} postId={editId ?? undefined} {...common} />
    ) : (
      <CaseStudyEditor key={editId ?? 'new'} caseStudyId={editId ?? undefined} {...common} />
    );
  }

  const noun = tab === 'posts' ? 'post' : 'case study';
  const filtered = q || status !== 'all' || cats.length > 0;

  const SortHead: React.FC<{ k: SortKey; children: React.ReactNode; className?: string }> = ({ k, children, className }) => {
    const active = sortKey === k;
    return (
      <th className={cn('h-9 border-b border-[color:var(--oa-line-2)] bg-[color:var(--oa-raised)] px-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.05em] text-[color:var(--oa-muted)]', className)}>
        <button
          type="button"
          onClick={() => patch({ sort: k === 'updatedAt' && active && sortDir === 'asc' ? null : k, dir: active && sortDir === 'desc' ? 'asc' : null })}
          className={cn('inline-flex items-center gap-1 uppercase hover:text-[color:var(--oa-text)]', active && 'text-[color:var(--oa-text)]')}
        >
          {children}
          {active ? sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" /> : null}
        </button>
      </th>
    );
  };
  const Head: React.FC<{ children?: React.ReactNode; className?: string }> = ({ children, className }) => (
    <th className={cn('h-9 border-b border-[color:var(--oa-line-2)] bg-[color:var(--oa-raised)] px-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.05em] text-[color:var(--oa-muted)]', className)}>{children}</th>
  );

  return (
    <div className="flex h-full flex-col bg-[color:var(--oa-card)] font-poppins text-[color:var(--oa-text)] antialiased">
      <PageBar
        title="Blog"
        meta={loading ? 'Loading…' : `${posts.length} posts · ${caseStudies.length} case studies`}
        actions={
          <>
            <ToolButton onClick={load} disabled={loading} title="Reload">
              <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
            </ToolButton>
            <PrimaryButton onClick={() => openEditor(null)}>
              <Plus className="h-4 w-4" />
              New {noun}
            </PrimaryButton>
          </>
        }
      >
        <Segmented<Tab>
          value={tab}
          onChange={(v) => patch({ tab: v === 'posts' ? null : v, cat: null })}
          options={[
            { value: 'posts', label: 'Posts', count: posts.length },
            { value: 'case-studies', label: 'Case studies', count: caseStudies.length },
          ]}
          className="ml-3"
        />
        <SearchInput value={q} onChange={(v) => patch({ q: v || null })} placeholder={tab === 'posts' ? 'Search title, tag, author' : 'Search title, client, industry'} className="ml-2" />
      </PageBar>

      <div className="flex h-11 shrink-0 items-center gap-2 overflow-x-auto border-b border-[color:var(--oa-line-2)] px-4 [scrollbar-width:none] [&>*]:shrink-0">
        <Segmented<StatusFilter>
          value={status}
          onChange={(v) => patch({ status: v === 'all' ? null : v })}
          options={[
            { value: 'all', label: 'All', count: counts.all },
            { value: 'published', label: 'Published', count: counts.published },
            { value: 'draft', label: 'Drafts', count: counts.draft },
          ]}
          className="h-7"
        />
        {tab === 'posts' ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <ToolButton active={cats.length > 0} className="h-7">
                {cats.length ? (cats.length === 1 ? categoryLabel(cats[0]) : `${cats.length} categories`) : 'Category'}
                <ChevronDown className="h-3.5 w-3.5" />
              </ToolButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52 rounded-xl">
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-[color:var(--oa-muted)]">Show categories</DropdownMenuLabel>
              {blogCategories.map((c) => (
                <DropdownMenuCheckboxItem
                  key={c.value}
                  checked={cats.includes(c.value)}
                  onSelect={(e) => e.preventDefault()}
                  onCheckedChange={(on) => patch({ cat: (on ? [...cats, c.value] : cats.filter((x) => x !== c.value)).join(',') || null })}
                >
                  {c.label}
                </DropdownMenuCheckboxItem>
              ))}
              {cats.length ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => patch({ cat: null })} className="text-[color:var(--oa-muted)]">
                    Clear
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
        {filtered ? (
          <button type="button" onClick={() => patch({ q: null, status: null, cat: null })} className="text-[12px] font-medium text-[color:var(--oa-muted)] hover:text-[color:var(--oa-text)]">
            Clear filters
          </button>
        ) : null}
        <span className="ml-auto text-[12px] tabular-nums text-[color:var(--oa-muted)]">
          {visible.length === rows.length ? `${rows.length} ${tab === 'posts' ? 'posts' : 'case studies'}` : `${visible.length} of ${rows.length}`}
        </span>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {/* Phones keep title, status and actions; the other columns carry oa-hide-phone. */}
        <table className="w-full min-w-[860px] border-separate border-spacing-0 text-[13px] max-[700px]:min-w-0 max-[700px]:table-fixed">
          <thead className="sticky top-0 z-[2]">
            <tr>
              <SortHead k="title" className="pl-4">Title</SortHead>
              <Head className="oa-hide-phone w-[200px]">{tab === 'posts' ? 'Category' : 'Client'}</Head>
              <Head className="oa-hide-phone w-[160px]">{tab === 'posts' ? 'Author' : 'Industry'}</Head>
              <Head className="w-[110px]">Status</Head>
              <SortHead k="publishedAt" className="oa-hide-phone w-[120px]">Published</SortHead>
              <SortHead k="updatedAt" className="oa-hide-phone w-[120px]">Updated</SortHead>
              <Head className="w-[52px]" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="h-14">
                  {Array.from({ length: 7 }).map((__, j) => (
                    <td key={j} className="border-b border-[color:var(--oa-line-2)] px-2.5">
                      <div className="h-3 animate-pulse rounded bg-[color:var(--oa-raised-2)]" style={{ width: j === 0 ? '60%' : '50%' }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : visible.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <EmptyState
                    icon={FileText}
                    title={filtered ? `No ${tab === 'posts' ? 'posts' : 'case studies'} match` : `No ${tab === 'posts' ? 'posts' : 'case studies'} yet`}
                    hint={filtered ? 'Loosen a filter or clear the search.' : `Write the first ${noun}.`}
                  >
                    {!filtered ? (
                      <PrimaryButton onClick={() => openEditor(null)}>
                        <Plus className="h-4 w-4" />
                        New {noun}
                      </PrimaryButton>
                    ) : null}
                  </EmptyState>
                </td>
              </tr>
            ) : (
              visible.map((r, i) => (
                <tr key={r.id} onClick={() => openEditor(r.id)} className="group/row h-14 cursor-pointer" style={{ animation: 'oa-rowin .5s var(--oa-ease) both', animationDelay: `${Math.min(i, 14) * 40}ms` }}>
                  <td className="border-b border-[color:var(--oa-line-2)] py-2 pl-4 pr-2.5 group-hover/row:bg-[color:var(--oa-accent-soft)]">
                    <div className="flex min-w-0 items-center gap-3">
                      <Thumb src={r.image} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate font-medium text-[color:var(--oa-text)]">{r.title || 'Untitled'}</span>
                          {r.featured ? <Star className="h-3.5 w-3.5 shrink-0 fill-[color:var(--oa-warn)] text-[color:var(--oa-warn)]" aria-label="Featured" /> : null}
                        </div>
                        <span className="block truncate font-mono text-[11px] text-[color:var(--oa-muted)]">{livePath(r)}</span>
                      </div>
                    </div>
                  </td>
                  <td className="oa-hide-phone border-b border-[color:var(--oa-line-2)] px-2.5 text-[color:var(--oa-text)] group-hover/row:bg-[color:var(--oa-accent-soft)]">{r.primary}</td>
                  <td className="oa-hide-phone truncate border-b border-[color:var(--oa-line-2)] px-2.5 text-[color:var(--oa-muted)] group-hover/row:bg-[color:var(--oa-accent-soft)]">{r.secondary}</td>
                  <td className="border-b border-[color:var(--oa-line-2)] px-2.5 group-hover/row:bg-[color:var(--oa-accent-soft)]">
                    <StatusTag status={r.status} />
                  </td>
                  <td className="oa-hide-phone border-b border-[color:var(--oa-line-2)] px-2.5 text-[12px] tabular-nums text-[color:var(--oa-muted)] group-hover/row:bg-[color:var(--oa-accent-soft)]">{r.publishedAt ? shortDay(r.publishedAt) : '—'}</td>
                  <td className="oa-hide-phone border-b border-[color:var(--oa-line-2)] px-2.5 text-[12px] tabular-nums text-[color:var(--oa-muted)] group-hover/row:bg-[color:var(--oa-accent-soft)]" title={new Date(r.updatedAt).toLocaleString('en-GB')}>
                    {ago(r.updatedAt)}
                  </td>
                  <td className="border-b border-[color:var(--oa-line-2)] px-2 text-right group-hover/row:bg-[color:var(--oa-accent-soft)]" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" aria-label={`Actions for ${r.title}`} className="grid h-7 w-7 place-items-center rounded-md text-[color:var(--oa-muted)] hover:bg-[color:var(--oa-raised-2)] hover:text-[color:var(--oa-text)] data-[state=open]:bg-[color:var(--oa-raised-2)]">
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44 rounded-xl">
                        <DropdownMenuItem onSelect={() => openEditor(r.id)} className="gap-2">
                          <Pencil className="h-3.5 w-3.5" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => window.open(livePath(r), '_blank', 'noopener')} disabled={r.status !== 'published'} className="gap-2">
                          <ExternalLink className="h-3.5 w-3.5" /> View live
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={() => setConfirm(r)} className="gap-2 text-[color:var(--oa-bad)] focus:text-[color:var(--oa-bad)]">
                          <Trash2 className="h-3.5 w-3.5" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && !deleting && setConfirm(null)}>
        <AlertDialogContent className="rounded-xl font-poppins">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-montserrat text-[16px] font-extrabold text-[color:var(--oa-text)]">Delete this {confirm?.kind === 'posts' ? 'post' : 'case study'}?</AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] text-[color:var(--oa-muted)]">
              “{confirm?.title}” will be removed from the site and the admin. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} className="h-8 rounded-md text-[12.5px]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                remove();
              }}
              disabled={deleting}
              className="h-8 rounded-md bg-[color:var(--oa-bad)] text-[12.5px] text-white hover:bg-[color:var(--oa-bad)]"
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminBlogEditor;
